import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Phone, Mail, CreditCard, Package, Truck,
  CheckCircle2, AlertCircle, Loader2, Copy,
} from 'lucide-react';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { OrderStatusPill, ORDER_STATUS_LABEL, PAYMENT_LABEL } from '@/components/shared/StatusPill';
import { adminApi } from '@/services/api';
import { formatNPR, formatDate } from '@/utils/format';
import { Button, Card, CardContent, Input, useConfirm } from '@nepal-ecom/ui';

const NEXT_STATUSES: Record<string, string[]> = {
  pending: ['processing', 'cancelled'],
  payment_pending: ['payment_confirmed', 'cancelled'],
  payment_confirmed: ['processing', 'cancelled'],
  processing: ['packed', 'cancelled'],
  packed: ['shipped', 'cancelled'],
  shipped: ['out_for_delivery', 'delivered'],
  out_for_delivery: ['delivered'],
  delivered: ['return_requested', 'refunded'],
  return_requested: ['return_approved', 'refunded'],
  return_approved: ['returned'],
  returned: ['refunded'],
  cancelled: [],
  refunded: [],
};

export function OrderDetail() {
  const confirm = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierName, setCourierName] = useState('');
  const [statusNote, setStatusNote] = useState('');

  const { data: order, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'order', id],
    queryFn: async () => {
      const res = await adminApi.getOrder(id!);
      return res.data.data as any;
    },
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: (next: string) => adminApi.updateOrderStatus(id!, next, statusNote || undefined),
    onSuccess: (_data, next) => {
      toast.success(`Order moved to "${ORDER_STATUS_LABEL[next] ?? next}"`);
      setStatusNote('');
      qc.invalidateQueries({ queryKey: ['admin', 'order', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Could not update status');
    },
  });

  const confirmPaymentMutation = useMutation({
    mutationFn: () => adminApi.confirmOrderPayment(id!),
    onSuccess: () => {
      toast.success('Payment confirmed');
      qc.invalidateQueries({ queryKey: ['admin', 'order', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Could not confirm payment');
    },
  });

  const trackingMutation = useMutation({
    mutationFn: () => adminApi.setOrderTracking(id!, trackingNumber, courierName),
    onSuccess: () => {
      toast.success('Tracking info saved');
      setTrackingNumber('');
      setCourierName('');
      qc.invalidateQueries({ queryKey: ['admin', 'order', id] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Could not save tracking');
    },
  });

  if (isLoading) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-32 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading order…
      </div>
    );
  }
  if (isError || !order) {
    return (
      <div className="animate-fade-in py-20 text-center">
        <AlertCircle className="h-10 w-10 mx-auto text-red-500 mb-3" />
        <p className="text-sm text-slate-600 mb-4">Could not load this order.</p>
        <Button size="sm" onClick={() => refetch()}>Retry</Button>
        <Button size="sm" variant="ghost" onClick={() => navigate('/orders')}>Back to orders</Button>
      </div>
    );
  }

  const allowedNext = NEXT_STATUSES[order.status] ?? [];

  return (
    <div className="animate-fade-in">
      <Breadcrumb items={[{ label: 'Orders', to: '/orders' }, { label: `#${order.orderNumber}` }]} />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button
            onClick={() => navigate('/orders')}
            className="text-xs text-slate-500 hover:text-primary-600 flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="h-3 w-3" /> Back to orders
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 tabular-nums">#{order.orderNumber}</h1>
            <button
              onClick={() => {
                navigator.clipboard.writeText(order.orderNumber);
                toast.success('Order number copied');
              }}
              className="text-slate-400 hover:text-slate-700"
              title="Copy order number"
            >
              <Copy className="h-4 w-4" />
            </button>
            <OrderStatusPill status={order.status} />
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Placed {formatDate(order.createdAt)}
            {order.deliveredAt && ` · delivered ${formatDate(order.deliveredAt)}`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: items + actions */}
        <div className="lg:col-span-2 space-y-5">
          {/* Items */}
          <Card>
            <CardContent className="p-0">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-500" />
                <h2 className="text-sm font-semibold text-slate-800">
                  Items <span className="text-slate-400 font-normal">({order.items?.length ?? 0})</span>
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {order.items?.map((item: any) => (
                  <div key={item.id} className="px-5 py-4 flex gap-4">
                    {item.productImage ? (
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="h-16 w-16 rounded-lg object-cover bg-slate-100 flex-shrink-0"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-lg bg-slate-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{item.productName}</p>
                      {item.variantName && <p className="text-xs text-slate-500">{item.variantName}</p>}
                      {item.vendor && (
                        <Link
                          to={`/vendors/${item.vendor.id}`}
                          className="text-xs text-primary-600 hover:underline"
                        >
                          {item.vendor.businessName}
                        </Link>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-slate-500 tabular-nums">
                        {item.quantity} × {formatNPR(Number(item.unitPrice))}
                      </p>
                      <p className="font-semibold text-slate-900 tabular-nums">
                        {formatNPR(Number(item.total))}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 space-y-1 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Subtotal</span>
                  <span className="tabular-nums">{formatNPR(Number(order.subtotal))}</span>
                </div>
                {Number(order.deliveryCharge) > 0 && (
                  <div className="flex justify-between text-slate-600">
                    <span>Delivery</span>
                    <span className="tabular-nums">{formatNPR(Number(order.deliveryCharge))}</span>
                  </div>
                )}
                {Number(order.discount) > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Discount</span>
                    <span className="tabular-nums">− {formatNPR(Number(order.discount))}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-slate-900 text-base pt-2 border-t border-slate-200 mt-2">
                  <span>Total</span>
                  <span className="tabular-nums">{formatNPR(Number(order.total))}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status actions */}
          <Card>
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-800 mb-1">Update status</h2>
              <p className="text-xs text-slate-500 mb-4">Move this order forward through fulfillment. Customer gets notified on each change.</p>

              {allowedNext.length === 0 ? (
                <p className="text-sm text-slate-500 italic">This order is in a final state.</p>
              ) : (
                <>
                  <Input
                    placeholder="Optional internal note for this status change…"
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    className="mb-3"
                  />
                  <div className="flex flex-wrap gap-2">
                    {allowedNext.map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={s === 'cancelled' ? 'outline' : 'default'}
                        disabled={statusMutation.isPending}
                        onClick={() => statusMutation.mutate(s)}
                      >
                        {statusMutation.isPending && statusMutation.variables === s && (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        )}
                        Mark as {ORDER_STATUS_LABEL[s] ?? s}
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Tracking */}
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="h-4 w-4 text-slate-500" />
                <h2 className="text-sm font-semibold text-slate-800">Shipment tracking</h2>
              </div>
              {order.trackingNumber ? (
                <div className="bg-slate-50 rounded-lg p-3 mb-3 text-sm">
                  <p className="text-slate-500 text-xs uppercase tracking-wide mb-1">Current tracking</p>
                  <p className="font-semibold text-slate-900 tabular-nums">{order.trackingNumber}</p>
                  {order.courierName && <p className="text-xs text-slate-600">via {order.courierName}</p>}
                </div>
              ) : (
                <p className="text-xs text-slate-500 mb-3">No tracking number set yet.</p>
              )}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Input
                  placeholder="Tracking number"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                />
                <Input
                  placeholder="Courier (e.g. Pathao)"
                  value={courierName}
                  onChange={(e) => setCourierName(e.target.value)}
                />
              </div>
              <Button
                size="sm"
                disabled={!trackingNumber.trim() || !courierName.trim() || trackingMutation.isPending}
                onClick={() => trackingMutation.mutate()}
              >
                {trackingMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Save tracking
              </Button>
            </CardContent>
          </Card>

          {/* Status timeline */}
          {order.statusHistory?.length > 0 && (
            <Card>
              <CardContent className="p-5">
                <h2 className="text-sm font-semibold text-slate-800 mb-4">Timeline</h2>
                <ol className="space-y-3">
                  {[...order.statusHistory].reverse().map((h: any, i: number) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-800">
                          {ORDER_STATUS_LABEL[h.status] ?? h.status}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(h.timestamp).toLocaleString()}
                          {h.note && ` · ${h.note}`}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: customer + address + payment */}
        <div className="space-y-5">
          <Card>
            <CardContent className="p-5">
              <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Customer</h3>
              {order.customer ? (
                <div className="space-y-2 text-sm">
                  <p className="font-semibold text-slate-900">{order.customer.fullName || 'Unnamed'}</p>
                  {order.customer.email && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Mail className="h-3.5 w-3.5 text-slate-400" />
                      <a href={`mailto:${order.customer.email}`} className="hover:underline">
                        {order.customer.email}
                      </a>
                    </p>
                  )}
                  {order.customer.phone && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Phone className="h-3.5 w-3.5 text-slate-400" />
                      {order.customer.phone}
                    </p>
                  )}
                  <Link
                    to={`/users/${order.customer.id}`}
                    className="inline-block text-xs text-primary-600 hover:underline mt-2"
                  >
                    View customer →
                  </Link>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Unknown</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5" /> Shipping address
              </h3>
              {order.shippingAddress ? (
                <div className="text-sm text-slate-700 space-y-0.5">
                  <p className="font-medium text-slate-900">{order.shippingAddress.fullName}</p>
                  <p>{order.shippingAddress.streetAddress}</p>
                  <p>
                    {order.shippingAddress.municipality}
                    {order.shippingAddress.wardNumber && `, Ward ${order.shippingAddress.wardNumber}`}
                  </p>
                  <p>{order.shippingAddress.district}, {order.shippingAddress.province}</p>
                  <p className="text-slate-500 text-xs pt-1">{order.shippingAddress.phone}</p>
                  {order.shippingAddress.landmark && <p className="text-xs italic text-slate-500">Landmark: {order.shippingAddress.landmark}</p>}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No address</p>
              )}
              {order.deliveryInstructions && (
                <p className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 italic">
                  "{order.deliveryInstructions}"
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-2">
                <CreditCard className="h-3.5 w-3.5" /> Payment
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Method</span>
                  <span className="font-medium text-slate-900">{PAYMENT_LABEL[order.paymentMethod] ?? order.paymentMethod}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Status</span>
                  <span className={order.isPaid ? 'text-emerald-700 font-medium' : 'text-amber-700 font-medium'}>
                    {order.isPaid ? 'Paid' : 'Pending'}
                  </span>
                </div>
                {order.paidAt && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Paid at</span>
                    <span className="text-slate-700 text-xs">{new Date(order.paidAt).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Bank-transfer receipt + confirm action */}
              {order.paymentMethod === 'bank_transfer' && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Customer receipt
                  </p>
                  {order.paymentReceiptUrl ? (
                    <a
                      href={order.paymentReceiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-md ring-1 ring-slate-200 bg-white overflow-hidden hover:ring-primary-300"
                    >
                      <img
                        src={order.paymentReceiptUrl}
                        alt="Payment receipt"
                        className="w-full max-h-64 object-contain"
                      />
                      <p className="text-[11px] text-center text-slate-500 px-2 py-1.5 border-t border-slate-100">
                        Click to open full size
                      </p>
                    </a>
                  ) : (
                    <p className="text-sm text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-3 py-2 rounded-md">
                      Customer has not uploaded a receipt yet.
                    </p>
                  )}
                </div>
              )}

              {/* Manual confirm — bank_transfer awaiting verification or COD before dispatch */}
              {!order.isPaid && (order.paymentMethod === 'bank_transfer' || order.paymentMethod === 'cod') && (
                <Button
                  className="w-full mt-4"
                  onClick={async () => {
                    if (order.paymentMethod === 'bank_transfer' && !order.paymentReceiptUrl) {
                      if (!(await confirm({
                        title: 'Confirm payment anyway?',
                        description: 'No receipt was uploaded for this bank transfer.',
                        confirmLabel: 'Confirm payment',
                      }))) return;
                    }
                    confirmPaymentMutation.mutate();
                  }}
                  disabled={confirmPaymentMutation.isPending}
                >
                  {confirmPaymentMutation.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4" />}
                  Confirm payment
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
